import { useSnackbar } from "notistack";
import { Box, Typography, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AgGrid from "../../../components/Table/AgGrid";
import { useDispatch } from "react-redux";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import { getItemsCurrentStockSerialNumber } from "../../../features/Inventory/Report/report-actions";
import { exportAsExcel } from "../../../utils/exporter";
import InventoryService from "../../../services/Inventory";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import { formatValueItemCurrentStockSerialNumber } from "./formatValue";
import TotalBox from "../../../components/UI/TotalBox";
import { filterParamsOptions } from "../../../utils/filterparams";
import isEmpty from "lodash/isEmpty";

const ItemCurrentStockSerialNumber = () => {
  const gridRef = useRef();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [sum, setSum] = useState(0);
  const [filterModel, setFilterModel] = useState({});
  // const { itemCurrentStockSerialNumber } = useSelector(
  //   (state) => state.inventoryReport
  // );

  const breadcrumbs = [
    {
      name: t("inventory.index"),
      to: "/inventory",
    },
    {
      name: t("inventory.report"),
      to: "/inventory/report",
    },
    {
      name: t("inventory.itemCurrentStockSerialNumber.index"),
    },
  ];

  const columnDefs = [
    {
      field: "item_group_sub_level_1_name",
      headerName: t("inventory.items.itemGroup") + " " + 1,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_document_id",
      headerName: t("inventory.items.itemId"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_name",
      headerName: t("inventory.items.itemName"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_description",
      headerName: t("inventory.items.itemDescription"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "current_quantity",
      headerName: t("inventory.itemCurrentStock.currentQuantity"),
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("number"),
      },
    },
    {
      field: "serial_number",
      headerName: t("inventory.serialNumber"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "batch_number",
      headerName: "Barcode",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    // {
    //   field: "warehouse_document_id",
    //   headerName: t("inventory.warehouseId"),
    //   filter: "agTextColumnFilter",
    // },
    {
      field: "warehouse_thai_name",
      headerName: t("inventory.warehouse"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "bin_location_document_id",
      headerName: t("inventory.location"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
  ];

  const exportHandler = async () => {
    try {
      const { results: itemCurrentStockBySerialNumberToExport } =
        await InventoryService.getItemCurrentStockSerialNumber({
          filterModel: filterModel,
        });
      const formattedItem = itemCurrentStockBySerialNumberToExport.map(
        (ics) => {
          const formatValue = formatValueItemCurrentStockSerialNumber(ics);
          return formatValue;
        }
      );
      exportAsExcel(
        formattedItem,
        t("inventory.itemCurrentStockSerialNumber.index")
      );
      enqueueSnackbar("นำออกรายงานสำเร็จ", {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar("นำออกรายงานไม่สำเร็จ", {
        variant: "error",
      });
    } finally {
      setIsLoadingExport(false);
    }
  };

  const datasource = {
    getRows(params) {
      const request = params.request;
      const sortbyId = [
        ...request.sortModel,
        { colId: "serial_number", sort: "asc" },
      ];
      setFilterModel(request.filterModel);
      dispatch(
        getItemsCurrentStockSerialNumber(
          {
            startRow: request.startRow,
            endRow: request.endRow,
            filterModel: request.filterModel,
            sortModel: sortbyId,
          },
          params,
          enqueueSnackbar
        )
      );
    },
  };

  const onGridReady = (params) => {
    params.api.setServerSideDatasource(datasource);
  };

  const getSumValue = useCallback(async (all, filterModel) => {
    const input = {
      startRow: 0,
      endRow: 99999,
      filterModel: {},
    };
    if (all) {
      const { results } =
        await InventoryService.getItemCurrentStockSerialNumber(input);
      const result = await results.reduce(
        (prev, curr) => prev + curr.current_quantity,
        0
      );
      setSum(result);
    } else {
      input.filterModel = filterModel;
      const { results } =
        await InventoryService.getItemCurrentStockSerialNumber(input);
      const result = await results.reduce(
        (prev, curr) => prev + curr.current_quantity,
        0
      );
      setSum(result);
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(filterModel)) {
      getSumValue(false, filterModel);
    } else getSumValue(true);
  }, [filterModel, getSumValue]);

  const onFilterReset = () => {
    if (gridRef) {
      gridRef.current.api.setFilterModel({});
    }
  };

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Grid container sx={{ mt: 1 }}>
        <Grid item xs={12} sm={4} md={3}>
          <TotalBox title="ยอดรวม" total={sum} />
        </Grid>
      </Grid>
      <Box sx={{ my: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">
          {t("inventory.itemCurrentStockSerialNumber.index")}
        </Typography>
        <CustomizedButton
          sx={{ mr: 2 }}
          title={t("inventory.exportReport")}
          variant="contained"
          onClick={exportHandler}
          disabled={isLoadingExport}
        />
      </Box>
      <Box mb={2} mr={2} alignSelf="flex-end">
        <CustomizedButton
          title={t("button.resetFilter")}
          variant="outlined"
          onClick={onFilterReset}
        />
      </Box>
      <AgGrid
        ref={gridRef}
        columnDefs={columnDefs}
        onGridReady={onGridReady}
        height={649}
      />
    </>
  );
};

export default ItemCurrentStockSerialNumber;
